import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { gridDataLoadingAtom } from '~/atoms/hooks/useGridData';
import { searchAtom } from './search';
import { productImagesAtom } from '~/atoms/productImages';
import { linePlanEntityAtom } from './linePlanEntity';
import { sortSeasons } from '~/utils/seasonSort';
const isCpsModel = (item) => {
    return (typeof item === 'object' &&
        item !== null &&
        'modelId' in item &&
        'modelName' in item);
};
const isCpsStyle = (item) => {
    return (typeof item === 'object' &&
        item !== null &&
        'styleNumber' in item &&
        'styleName' in item);
};
const isProject = (item) => {
    return (typeof item === 'object' &&
        item !== null &&
        'id' in item &&
        'projectName' in item);
};
const isExpression = (item) => {
    return (typeof item === 'object' &&
        item !== null &&
        'expressionName' in item &&
        'expressionType' in item);
};
function transformProjectToCard(project, productImages) {
    const modelId = 0;
    const projectImages = productImages.get(modelId.toString()) || [];
    const formattedImages = projectImages.map((img) => ({
        url: img.url,
        alt: project.projectName,
        viewCategory: img.viewCategory
    }));
    const defaultImage = {
        url: '/assets/projects/placeholder-project.png',
        alt: project.projectName,
        viewCategory: 'main'
    };
    const imagesToUse = formattedImages.length > 0 ? formattedImages : [defaultImage];
    const sortedSeasons = project.season ? sortSeasons(project.season) : undefined;
    return {
        id: project.id.toString(),
        modelId,
        projectName: project.projectName,
        modelName: project.projectName,
        franchise: project.franchise,
        season: sortedSeasons,
        creationTrack: project.creationTrack,
        projectedUnit: project.projectedUnit,
        genderVariations: project.genderVariations,
        msrp: project.msrp,
        silhouette: project.silo?.label || '',
        silo: project.silo?.label || '',
        sportActivity: project.sportActivity?.label || '',
        gender: project.genderVariations?.map((g) => g.label).join(', ') || '',
        age: '',
        tags: [
            project.msrp ? `$${project.msrp}` : '',
            project.strategicIntent?.label || ''
        ].filter(Boolean),
        colorways: [],
        images: imagesToUse
    };
}
function transformExpressionToCard(expression) {
    const expressionImage = {
        url: '/assets/mockExpressionMain.png',
        alt: expression.expressionName,
        viewCategory: 'main'
    };
    const tags = [];
    if (expression.expressionType?.label)
        tags.push(expression.expressionType.label);
    if (expression.lifecycle?.label)
        tags.push(expression.lifecycle.label);
    if (expression.specialEdition)
        tags.push('Special Edition');
    if (expression.seasons && expression.seasons.length > 0) {
        tags.push(...expression.seasons.map((s) => s.label));
    }
    const sortedSeasons = expression.seasons
        ? sortSeasons(expression.seasons)
        : undefined;
    return {
        id: String(expression.id),
        expressionName: expression.expressionName,
        modelName: expression.expressionName,
        season: sortedSeasons,
        expressionType: expression.expressionType,
        gender: expression.gender,
        upcharge: expression.upcharge,
        lifecycle: expression.lifecycle,
        distributionIntent: expression.distributionIntent,
        keyDistro: expression.keyDistro,
        hasApparelConnection: expression.hasApparelConnection,
        keyApparelConnect: expression.keyApparelConnect,
        forecast: expression.forecast,
        brand: expression.brand?.label || '',
        franchise: expression.brand?.label || '',
        division: expression.division?.label || '',
        sportActivity: expression.division?.label || 'Expression',
        fieldOfPlay: expression.fieldOfPlay?.label || '',
        age: expression.age?.label || '',
        tags,
        colorways: [],
        images: [expressionImage],
        museDoors: expression.museDoors
    };
}
function transformModelToCard(model, productImages) {
    const productCount = model.products?.length || 0;
    const allColorways = model.products?.flatMap((product) => {
        const colors = [
            product.primaryColor,
            product.secondaryColor,
            product.tertiaryColor,
            product.logoAccentColor,
            product.logoColor
        ].filter(Boolean);
        return colors;
    }) || [];
    const uniqueColorways = [...new Set(allColorways)];
    const allTags = model.products?.flatMap((product) => product.features || []) || [];
    const uniqueTags = [...new Set(allTags)];
    const modelImages = productImages.get(model.modelId.toString()) || [];
    const formattedImages = modelImages.map((img) => ({
        url: img.url,
        alt: model.modelName,
        viewCategory: img.viewCategory
    }));
    return {
        id: model.modelId.toString(),
        modelId: model.modelId,
        modelName: model.modelName,
        age: model.age,
        construction: model.construction,
        division: model.division,
        fitPreference: model.fitPreference,
        franchise: model.franchise,
        franchiseName: model.franchise,
        gender: model.gender,
        consumer: model.gender,
        midsoleDescription: model.midsoleDescription,
        modelGroups: model.modelGroups,
        outsoleDescription: model.outsoleDescription,
        platforms: model.platforms,
        plate: model.plate,
        rise: model.rise,
        silhouetteType: model.silhouetteType,
        silhouette: model.silhouette,
        sportActivity: model.sportActivity,
        category: model.sportActivity,
        status: model.status,
        subBrand: model.subBrand,
        family: model.family,
        fieldOfPlay: model.family,
        subFamily: model.subFamily,
        upperDescription: model.upperDescription,
        lastModified: model.lastModified,
        lastModifiedBy: model.lastModifiedBy,
        productCount,
        tags: uniqueTags.length > 0 ? uniqueTags : [],
        colorways: uniqueColorways,
        images: formattedImages
    };
}
function transformStyleToCard(style, productImages) {
    const styleImages = productImages.get(style.modelId.toString()) || [];
    const formattedStyleImages = styleImages.map((img) => ({
        url: img.url,
        alt: style.styleName,
        viewCategory: img.viewCategory
    }));
    const styleProductCount = (style.linkedStyles?.length || 0) + 1;
    return {
        id: style.styleNumber,
        styleNumber: style.styleNumber,
        styleName: style.styleName,
        modelName: style.styleName,
        modelId: style.modelId,
        modificationDate: style.modificationDate,
        modifiedBy: style.modifiedBy,
        deliveryUnit: style.deliveryUnit,
        dimension: style.dimension,
        gender: style.dimension,
        fit: style.fit,
        age: style.fit,
        licensed: style.licensed ? 'Yes' : 'No',
        merchandisingClassification: style.merchandisingClassification,
        segment: style.segment,
        franchise: style.segment,
        franchiseName: style.styleName,
        sizeScale: style.sizeScale,
        silhouette: style.sizeScale,
        styleSportActivity: style.styleSportActivity,
        silhouetteType: style.styleSportActivity,
        category: style.styleSportActivity,
        sport: style.styleSportActivity,
        entryIntoMarket: style.entryIntoMarket,
        productCount: styleProductCount,
        tags: [style.entryIntoMarket].filter(Boolean),
        colorways: [],
        images: formattedStyleImages
    };
}
export const cardScaleAtom = atomWithStorage('cardView_scale', 'medium');
const baseGroupByAtom = atomWithStorage('cardView_groupBy', 'none');
export const groupByAtom = atom((get) => {
    const stored = get(baseGroupByAtom);
    const linePlanEntity = get(linePlanEntityAtom);
    const defaultGroupBy = linePlanEntity === 'project'
        ? 'franchise'
        : linePlanEntity === 'expression'
            ? 'season'
            : 'none';
    if (typeof stored === 'object' && stored !== null) {
        return stored[linePlanEntity] !== undefined
            ? stored[linePlanEntity]
            : defaultGroupBy;
    }
    if (stored === 'none' && defaultGroupBy !== 'none') {
        return defaultGroupBy;
    }
    return stored;
}, (get, set, newValue) => {
    const linePlanEntity = get(linePlanEntityAtom);
    const stored = get(baseGroupByAtom);
    if (typeof stored === 'object' && stored !== null) {
        set(baseGroupByAtom, {
            ...stored,
            [linePlanEntity]: newValue
        });
    }
    else {
        set(baseGroupByAtom, {
            [linePlanEntity]: newValue
        });
    }
});
export const viewLayoutAtom = atomWithStorage('cardView_layout', 'grid');
export const cardSubViewAtom = atomWithStorage('cardView_subView', 'standard');
const defaultCustomGroups = [
    { id: 'ungrouped', title: 'Ungrouped', order: 999 }
];
export const customGroupsAtom = atomWithStorage('cardView_customGroups', defaultCustomGroups);
export const cardGroupAssignmentsAtom = atomWithStorage('cardView_groupAssignments', {});
export const cardOrderAtom = atomWithStorage('cardView_cardOrder', {});
// UI state for custom groups managers (not persisted)
export const customGroupsManagerOpenAtom = atom(false);
export const groupOrderManagerOpenAtom = atom(false);
export const filteredCardsAtom = atom((get) => {
    const loadedData = get(gridDataLoadingAtom);
    const searchTerm = get(searchAtom);
    const productImages = get(productImagesAtom);
    if (loadedData.state === 'hasData') {
        const validItems = loadedData.data.filter((item) => {
            return (isCpsModel(item) ||
                isCpsStyle(item) ||
                isProject(item) ||
                isExpression(item));
        });
        let filteredItems = validItems.filter((item) => {
            if (!searchTerm) {
                return true;
            }
            if (isCpsModel(item)) {
                return item.modelName.toLowerCase().includes(searchTerm.toLowerCase());
            }
            if (isCpsStyle(item)) {
                return item.styleName.toLowerCase().includes(searchTerm.toLowerCase());
            }
            if (isProject(item)) {
                return item.projectName
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase());
            }
            if (isExpression(item)) {
                return item.expressionName
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase());
            }
            return false;
        });
        const transformed = filteredItems.map((item) => {
            if (isCpsModel(item)) {
                return transformModelToCard(item, productImages);
            }
            if (isCpsStyle(item)) {
                return transformStyleToCard(item, productImages);
            }
            if (isProject(item)) {
                return transformProjectToCard(item, productImages);
            }
            if (isExpression(item)) {
                return transformExpressionToCard(item);
            }
            throw new Error('Unknown item type');
        });
        return transformed;
    }
    return [];
});
export const groupedCardsAtom = atom((get) => {
    const filteredCards = get(filteredCardsAtom);
    const groupBy = get(groupByAtom);
    if (groupBy === 'none' || groupBy === 'custom') {
        return { none: filteredCards };
    }
    if (groupBy === 'franchise') {
        const grouped = {};
        filteredCards.forEach((card) => {
            let groupValue = 'Uncategorized';
            if (typeof card.franchise === 'string') {
                groupValue = card.franchise || 'Uncategorized';
            }
            else if (card.franchise && typeof card.franchise === 'object') {
                const franchiseObj = card.franchise;
                groupValue = franchiseObj?.label || franchiseObj?.id || 'Uncategorized';
            }
            if (!grouped[groupValue]) {
                grouped[groupValue] = [];
            }
            grouped[groupValue].push(card);
        });
        return grouped;
    }
    if (groupBy === 'model') {
        const grouped = {};
        filteredCards.forEach((card) => {
            const groupValue = ('modelName' in card
                ? card.modelName
                : card.franchiseName) || 'Uncategorized';
            if (!grouped[groupValue]) {
                grouped[groupValue] = [];
            }
            grouped[groupValue].push(card);
        });
        return grouped;
    }
    if (groupBy === 'season') {
        const groupedByLabel = {};
        const labelToSeasonId = {};
        filteredCards.forEach((card) => {
            const seasonArray = card.season;
            const firstSeasonObj = seasonArray?.[0];
            const seasonId = firstSeasonObj?.id && typeof firstSeasonObj.id === 'string'
                ? firstSeasonObj.id.toUpperCase()
                : null;
            const seasonLabel = firstSeasonObj?.label || 'No Season';
            if (!groupedByLabel[seasonLabel]) {
                groupedByLabel[seasonLabel] = [];
            }
            if (seasonId && !labelToSeasonId[seasonLabel]) {
                labelToSeasonId[seasonLabel] = seasonId;
            }
            groupedByLabel[seasonLabel].push(card);
        });
        const SEASON_ORDER = {
            SP: 1,
            SU: 2,
            FA: 3,
            HO: 4
        };
        const sortedGrouped = {};
        const groupLabels = Object.keys(groupedByLabel).filter((label) => label !== 'No Season');
        const sortedLabels = groupLabels.sort((labelA, labelB) => {
            const seasonIdA = labelToSeasonId[labelA];
            const seasonIdB = labelToSeasonId[labelB];
            if (!seasonIdA)
                return 1;
            if (!seasonIdB)
                return -1;
            const codeA = seasonIdA.substring(0, 2);
            const codeB = seasonIdB.substring(0, 2);
            const yearA = parseInt(seasonIdA.substring(2, 4), 10);
            const yearB = parseInt(seasonIdB.substring(2, 4), 10);
            const isValidA = !isNaN(yearA) && SEASON_ORDER[codeA] !== undefined;
            const isValidB = !isNaN(yearB) && SEASON_ORDER[codeB] !== undefined;
            if (!isValidA && !isValidB)
                return 0;
            if (!isValidA)
                return 1;
            if (!isValidB)
                return -1;
            if (yearA !== yearB)
                return yearA - yearB;
            return SEASON_ORDER[codeA] - SEASON_ORDER[codeB];
        });
        sortedLabels.forEach((label) => {
            sortedGrouped[label] = groupedByLabel[label];
        });
        if (groupedByLabel['No Season']) {
            sortedGrouped['No Season'] = groupedByLabel['No Season'];
        }
        return sortedGrouped;
    }
    const grouped = {};
    filteredCards.forEach((card) => {
        const groupValue = card[groupBy] || 'Uncategorized';
        if (!grouped[groupValue]) {
            grouped[groupValue] = [];
        }
        grouped[groupValue].push(card);
    });
    return grouped;
});
